import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="post-harvest--drying-output"
// Manages drying output step: output containers per drying unit, budget tracking, and serialization.
export default class extends Controller {
  static targets = ["groupCard", "outputContainersDataField"];
  static values = {
    existingIdentifiers: Array,
  };

  connect() {
    this.formElement = this.element.closest("form");
    this.beforeSubmitHandler = this.handleSubmit.bind(this);
    if (this.formElement) {
      this.formElement.addEventListener("submit", this.beforeSubmitHandler);
    }
    this.updateAllBudgets();
    this.serialize();
  }

  disconnect() {
    if (this.formElement) {
      this.formElement.removeEventListener("submit", this.beforeSubmitHandler);
    }
  }

  handleSubmit(event) {
    this.serialize();
    if (this.hasOverBudgetUnits()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  hasOverBudgetUnits() {
    let overBudget = false;
    this.groupCardTargets.forEach((card) => {
      const budget = parseFloat(card.dataset.allocatedWeight) || 0;
      if (budget <= 0) return;
      const rows = card.querySelectorAll("[data-output-row]");
      let used = 0;
      rows.forEach((row) => {
        used +=
          parseFloat(
            row.querySelector("[data-field=net_weight_value]")?.value,
          ) || 0;
      });
      if (used > budget) overBudget = true;
    });
    return overBudget;
  }

  update() {
    this.updateAllBudgets();
    this.serialize();
  }

  addOutput(event) {
    event.preventDefault();
    const card = event.target.closest("[data-drying-group-card]");
    if (!card) return;

    const template = card.querySelector("[data-output-template]");
    const list = card.querySelector("[data-output-list]");
    if (!template || !list) return;

    const nextIdentifier = this.nextAvailableIdentifier();
    const content = template.content.firstElementChild.outerHTML
      .replace(/NEW_ID/g, Date.now())
      .replace(/D-XX/g, nextIdentifier);
    list.insertAdjacentHTML("beforeend", content);
    this.updateAllBudgets();
    this.serialize();
  }

  removeOutput(event) {
    event.preventDefault();
    const row = event.target.closest("[data-output-row]");
    if (row) {
      row.remove();
      this.updateAllBudgets();
      this.serialize();
    }
  }

  updateAllBudgets() {
    this.groupCardTargets.forEach((card) => {
      const budget = parseFloat(card.dataset.allocatedWeight) || 0;
      const unit = card.dataset.allocatedUnit || "g";
      const rows = card.querySelectorAll("[data-output-row]");
      let used = 0;
      rows.forEach((row) => {
        used +=
          parseFloat(
            row.querySelector("[data-field=net_weight_value]")?.value,
          ) || 0;
      });

      const budgetDisplay = card.querySelector("[data-budget-display]");
      if (budgetDisplay) {
        budgetDisplay.textContent = `${used.toFixed(2)} / ${budget.toFixed(2)} ${unit}`;
        const overBudget = used > budget && budget > 0;
        budgetDisplay.classList.toggle("text-error", overBudget);
        budgetDisplay.classList.toggle("font-bold", overBudget);
      }

      const addButton = card.querySelector("[data-add-output-button]");
      if (addButton) {
        const full = used >= budget && budget > 0;
        addButton.disabled = full;
      }

      const fullBadge = card.querySelector("[data-budget-full-badge]");
      if (fullBadge) {
        const overBudget = used > budget && budget > 0;
        const full = used >= budget && budget > 0;
        fullBadge.classList.toggle("hidden", !full);
        fullBadge.classList.toggle("badge-warning", full && !overBudget);
        fullBadge.classList.toggle("badge-error", overBudget);
        fullBadge.textContent = overBudget
          ? card.dataset.budgetExceededText || "Budget exceeded"
          : card.dataset.budgetFullText || "Budget fully allocated";
      }
    });
  }

  nextAvailableIdentifier() {
    const batchIdentifiers = this.hasExistingIdentifiersValue
      ? this.existingIdentifiersValue
      : [];
    const usedIdentifiers = new Set([
      ...batchIdentifiers.map((id) => this.normalizeId(id)),
      ...this.currentOutputIdentifiers(),
    ]);

    let n = 1;
    while (true) {
      const candidate = `D-${String(n).padStart(2, "0")}`;
      if (!usedIdentifiers.has(this.normalizeId(candidate))) return candidate;
      n++;
    }
  }

  currentOutputIdentifiers() {
    return Array.from(this.element.querySelectorAll("[data-output-row]"))
      .map((row) => row.querySelector("[data-field=identifier]")?.value || "")
      .map((id) => this.normalizeId(id))
      .filter((id) => id.length > 0);
  }

  normalizeId(id) {
    return String(id || "")
      .trim()
      .toUpperCase();
  }

  serialize() {
    const outputs = [];
    this.groupCardTargets.forEach((card) => {
      const dryingUnitId = card.dataset.dryingUnitId;
      card.querySelectorAll("[data-output-row]").forEach((row) => {
        const identifier =
          row.querySelector("[data-field=identifier]")?.value || "";
        const netWeight =
          parseFloat(
            row.querySelector("[data-field=net_weight_value]")?.value,
          ) || 0;
        const netUnit =
          row.querySelector("[data-field=net_weight_unit]")?.value || "g";
        const dryingPhotoSignedId =
          row.querySelector(
            "[data-post-harvest--photo-upload-target='signedId']",
          )?.value || "";

        outputs.push({
          identifier,
          net_weight_value: netWeight,
          net_weight_unit: netUnit,
          drying_photo_signed_id: dryingPhotoSignedId,
          drying_unit_id: dryingUnitId,
        });
      });
    });

    if (this.hasOutputContainersDataFieldTarget) {
      this.outputContainersDataFieldTarget.value = JSON.stringify(outputs);
    }
  }
}
